import { useMemo } from 'react';
import { useUser } from '../components/UserContext';
import {
  useClients, useContracts, useConvenios, usePendientesRaw,
} from '../contexts/DataContext';
import { decorarPendiente, isAbierto } from '../utils/pendientes';

// Frozen empty list — see useOrgPendientes.
const VACIO = [];

/**
 * Org-scoped pendientes for broker users, live.
 *
 * Reads the SINGLE listener in DataProvider instead of opening one here. It
 * used to subscribe per call, which meant the Pendientes page, the Pulso tab
 * and each mounted PendientesPanel each held an identical onSnapshot on the
 * same query — and panels mount/unmount with their modals. That churn trips
 * "FIRESTORE INTERNAL ASSERTION FAILED: Unexpected state" in the SDK's watch
 * stream; the comisión ledger listener carries the same warning for the same
 * reason. Call this from as many components as you like.
 *
 * Returns null while loading so callers can gate their first paint.
 *
 * @return {Array<Object>|null} raw docs
 */
export function useOrgPendientes() {
  const { isClient } = useUser();
  const { pendientes, pendientesLoading } = usePendientesRaw();
  // Stable identity for the client-user case so downstream memos don't churn.
  if (isClient) return VACIO;
  return pendientesLoading ? null : pendientes;
}

/**
 * Broker pendientes decorated with the cliente / contrato / convenio names the
 * list renders, and split into open vs closed.
 *
 * `nowMs` is frozen per render pass so every row in one paint is classified
 * against the same instant.
 *
 * @param {Object} [options]
 * @param {boolean} [options.soloAbiertos]  - drop resueltos/cancelados
 * @return {{rows: Array<Object>|null, abiertos: Array<Object>, loading: boolean}}
 */
export function usePendientesDecorados({ soloAbiertos = false } = {}) {
  const raw = useOrgPendientes();
  const { clientsById } = useClients();
  const { contractsById } = useContracts();
  const { conveniosById } = useConvenios();

  const decorated = useMemo(() => {
    if (raw === null) return null;
    const now = Date.now();
    return raw.map((p) => {
      const cliente = clientsById[p.clienteId] || {};
      const contrato = contractsById[p.contratoId] || {};
      const convenio = conveniosById[p.convenioId] || {};
      return decorarPendiente(p, {
        // Empty for an org-level row: decorarPendiente labels those itself.
        clientName: p.convenioId
          ? (convenio.nombre || 'Convenio')
          : p.clienteId
            ? (cliente.nombreEmpresa || 'Sin fiado')
            : '',
        contractTitle: contrato.tituloContrato?.value || '',
        convenioNombre: convenio.nombre || '',
        assignedUsers: [
          ...(cliente.assignedUsers || []),
          ...(contrato.assignedUsers || []),
        ].filter((v, i, a) => a.indexOf(v) === i),
      }, now);
    });
  }, [raw, clientsById, contractsById, conveniosById]);

  const abiertos = useMemo(
    () => (decorated || []).filter(isAbierto),
    [decorated]
  );

  return {
    rows: soloAbiertos ? abiertos : decorated,
    abiertos,
    loading: raw === null,
  };
}

export default usePendientesDecorados;
